import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    CheckCircle2,
    Info,
    Mail,
    Send,
    Shield,
    ShieldOff,
} from "lucide-react";
import api from "@/lib/api";

/**
 * NotificationRoutingCard
 *
 * Admin-only card that pins the three notification recipients (primary,
 * temporary backup, dry-run shadow), shows live-email status, and exposes
 * a dry-run-only "Send Dry-Run Test Ping" button. Mounted inside
 * `/admin/verification`.
 *
 * Wording rules (Feb 2026):
 *   - Always make `info@a1screening247.com` the primary visible address.
 *   - `a1screening247@gmail.com` is admin-only — NEVER rendered on a
 *     public marketing page. This component lives behind the admin auth
 *     wall so its visibility is already correctly scoped.
 */
export default function NotificationRoutingCard() {
    const [routing, setRouting] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [lastPing, setLastPing] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            const r = await api.get("/admin/notifications/routing");
            setRouting(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't load notification routing.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const sendPing = async () => {
        setBusy(true);
        setError("");
        try {
            const r = await api.post("/admin/notifications/dry-run-ping");
            setLastPing(r.data);
            await load();
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't send dry-run test ping.");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="a1-card mb-6" data-testid="notification-routing-loading">
                <p className="text-sm text-[var(--color-text-muted)]">Loading notification routing…</p>
            </div>
        );
    }

    if (!routing) {
        return (
            <div className="a1-card mb-6" data-testid="notification-routing-error">
                <p className="text-sm" style={{ color: "var(--color-storm)" }}>{error || "Notification routing unavailable."}</p>
            </div>
        );
    }

    return (
        <div className="a1-card mb-6" data-testid="notification-routing-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                <div className="flex items-center gap-2">
                    <Mail size={20} className="text-[var(--color-accent)]" />
                    <h2 className="text-xl m-0">Notification Routing</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                    <StatusBadge
                        ok={!!routing.primary_recipient}
                        okLabel="Primary configured"
                        failLabel="Primary missing"
                        testId="badge-primary"
                    />
                    <StatusBadge
                        ok={!!routing.backup_recipient}
                        okLabel="Backup configured"
                        failLabel="No backup configured"
                        warnIfMissing
                        testId="badge-backup"
                    />
                    <StatusBadge
                        ok={!!routing.sms_alert_recipient}
                        okLabel="SMS recipient configured"
                        failLabel="No SMS recipient"
                        warnIfMissing
                        testId="badge-sms"
                    />
                    <StatusBadge
                        ok={routing.a2p_status === "approved"}
                        okLabel={routing.a2p_provider ? `A2P approved · ${routing.a2p_provider}` : "A2P approved"}
                        failLabel="A2P pending"
                        warnIfMissing
                        testId="badge-a2p"
                    />
                    <StatusBadge
                        ok={routing.email_domain_status === "verified"}
                        okLabel={routing.email_provider ? `Email domain verified · ${routing.email_provider}` : "Email domain verified"}
                        failLabel="Email domain pending"
                        warnIfMissing
                        testId="badge-email-domain"
                    />
                    <StatusBadge
                        ok={!!routing.dry_run_active}
                        okLabel="Dry-run active"
                        failLabel="Dry-run OFF"
                        testId="badge-dry-run"
                    />
                    <StatusBadge
                        ok={!routing.live_email_active}
                        okLabel="Live email inactive"
                        failLabel="Live email ACTIVE"
                        invertColors
                        testId="badge-live-email"
                    />
                    <StatusBadge
                        ok={!routing.live_sms_active}
                        okLabel="Live SMS inactive"
                        failLabel="Live SMS ACTIVE"
                        invertColors
                        testId="badge-live-sms"
                    />
                </div>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] mb-4">
                Where admin alert email + SMS is routed. Tester-week feedback (Feb 2026): <strong>John prefers both SMS and email alerts.</strong> Both channels will fire once Resend + Twilio are activated.
            </p>

            {/* Warnings */}
            {Array.isArray(routing.warnings) && routing.warnings.length ? (
                <div className="mb-4 grid gap-2" data-testid="notification-routing-warnings">
                    {routing.warnings.map((w, idx) => <WarningRow key={idx} warning={w} />)}
                </div>
            ) : null}

            {/* Recipients */}
            <div className="grid gap-3 mb-4" data-testid="notification-routing-rows">
                <RecipientRow
                    label="Primary alert recipient"
                    address={routing.primary_recipient}
                    role="primary"
                    helper="Used as the From / To address for every admin alert when live email is activated."
                    testId="recipient-primary"
                />
                <RecipientRow
                    label="Temporary backup recipient"
                    address={routing.backup_recipient}
                    role="backup"
                    helper="Admin-only fallback shown during the email-migration window. Never displayed on public marketing pages."
                    testId="recipient-backup"
                />
                <RecipientRow
                    label="SMS alert recipient (owner phone)"
                    address={routing.sms_alert_recipient}
                    role="sms"
                    helper="Owner business phone. Tester-week feedback (Feb 2026): John prefers BOTH email and SMS for new-lead alerts."
                    testId="recipient-sms"
                />
                <RecipientRow
                    label="Dry-run shadow recipient"
                    address={routing.dry_run_shadow_recipient}
                    role="shadow"
                    helper="Capture inbox used while Dry-Run Mode is on. Every notification's `recipient` field is rewritten to this address — no live email is sent."
                    testId="recipient-shadow"
                />
            </div>

            {/* Dry-run ping */}
            <div className="border-t pt-4 mt-2" style={{ borderColor: "var(--color-line)" }}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <button
                        onClick={sendPing}
                        disabled={busy || !routing.dry_run_active || !routing.primary_recipient}
                        className="a1-btn-primary"
                        data-testid="notification-routing-ping-btn"
                    >
                        <Send size={16} /> {busy ? "Sending…" : "Send Dry-Run Test Ping"}
                    </button>
                    <p className="text-xs text-[var(--color-text-muted)] max-w-xl leading-relaxed">
                        Creates one row in <strong>notification_events</strong> + one row in the <strong>Automation Event Log</strong> with <code>template_id=admin-ping-test</code>. No real email is sent. Visible at <Link className="underline" to="/admin/shadow-inbox" data-testid="notification-routing-link-shadow-inbox">/admin/shadow-inbox</Link> and <Link className="underline" to="/admin/automation-events?event_type=admin_ping_test">/admin/automation-events</Link>.
                    </p>
                </div>
                {!routing.dry_run_active ? (
                    <p className="text-xs mt-2" style={{ color: "var(--color-storm)" }}>
                        Dry-Run Mode is OFF — test ping is intentionally disabled. Re-enable Dry-Run Mode to run a safe routing check.
                    </p>
                ) : null}

                {error ? (
                    <p className="text-sm mt-3" style={{ color: "var(--color-storm)" }} data-testid="notification-routing-error-msg">
                        {error}
                    </p>
                ) : null}

                {lastPing?.preview ? (
                    <div
                        className="mt-4 p-3 rounded-md text-xs grid gap-1"
                        style={{ background: "var(--color-bg-alt)", border: "1px solid var(--color-line)" }}
                        data-testid="notification-routing-last-ping"
                    >
                        <p className="font-semibold text-sm flex items-center gap-2">
                            <CheckCircle2 size={14} className="text-[var(--color-accent)]" />
                            Dry-run test ping logged
                        </p>
                        <p>template_id: <code>{lastPing.preview.template_id}</code></p>
                        <p>primary_recipient: <code>{lastPing.preview.primary_recipient}</code></p>
                        <p>backup_recipient: <code>{lastPing.preview.backup_recipient || "(none)"}</code></p>
                        <p>dry_run_shadow_recipient: <code>{lastPing.preview.dry_run_shadow_recipient || "(none)"}</code></p>
                        <p>actor: <code>{lastPing.preview.actor}</code></p>
                        <p>sent_at: <code>{lastPing.preview.sent_at}</code></p>
                        <p style={{ color: "var(--color-accent)" }}>DRY-RUN — no real email was sent.</p>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function StatusBadge({ ok, okLabel, failLabel, warnIfMissing, invertColors, testId }) {
    // Default: green when ok, red when not. invertColors flips so e.g. "Live email
    // inactive" reads as the safe state (green) while we're pre-launch.
    let good = ok;
    if (invertColors) good = ok;  // semantics already correct from caller's POV
    const bg = good
        ? "rgba(40, 216, 224, 0.12)"
        : warnIfMissing
            ? "rgba(255, 214, 95, 0.15)"
            : "rgba(231, 119, 78, 0.15)";
    const color = good
        ? "var(--color-accent)"
        : warnIfMissing
            ? "var(--color-warning)"
            : "var(--color-storm)";
    return (
        <span
            className="a1-badge"
            style={{ background: bg, color, border: `1px solid ${color}` }}
            data-testid={testId}
        >
            {good ? <CheckCircle2 size={12} /> : warnIfMissing ? <Info size={12} /> : <AlertTriangle size={12} />}
            <span style={{ marginLeft: 4 }}>{good ? okLabel : failLabel}</span>
        </span>
    );
}

function WarningRow({ warning }) {
    const isCritical = warning.level === "critical";
    const Icon = isCritical ? AlertTriangle : Info;
    const color = isCritical ? "var(--color-storm)" : "var(--color-accent)";
    const bg = isCritical ? "rgba(231, 119, 78, 0.08)" : "rgba(40, 216, 224, 0.06)";
    return (
        <div
            className="flex items-start gap-2 p-3 rounded-md"
            style={{ background: bg, border: `1px solid ${color}` }}
            data-testid={`notification-routing-warning-${warning.level}`}
        >
            <Icon size={16} style={{ color, flexShrink: 0, marginTop: 2 }} />
            <p className="text-sm leading-relaxed" style={{ color }}>{warning.message}</p>
        </div>
    );
}

function RecipientRow({ label, address, role, helper, testId }) {
    const RoleIcon = role === "shadow" ? ShieldOff : Shield;
    return (
        <div
            className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-md border"
            style={{ borderColor: "var(--color-line)", background: "var(--color-bg-card)" }}
            data-testid={testId}
        >
            <div className="flex items-center gap-2 sm:min-w-[220px]">
                <RoleIcon size={16} className="text-[var(--color-text-muted)] shrink-0" />
                <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-text-muted)] m-0">{label}</p>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-mono break-all" data-testid={`${testId}-address`}>
                    {address || <span style={{ color: "var(--color-storm)" }}>(not configured)</span>}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] leading-relaxed mt-0.5">{helper}</p>
            </div>
        </div>
    );
}
